import type { CSSProperties } from 'react'
import { formatMoney } from '../../lib/format'

export type EventKind = 'print' | 'sale' | 'expense'

export interface EventRowProps {
  kind: EventKind
  title: string
  amount: number
  onClick?: () => void
}

const kindStyles: Record<EventKind, { glyph: string; bg: string; fg: string }> = {
  print:   { glyph: 'P', bg: 'var(--color-primary-soft)', fg: 'var(--color-primary-text)' },
  sale:    { glyph: '$', bg: 'var(--color-success-soft)', fg: 'var(--color-success-text)' },
  expense: { glyph: 'E', bg: 'var(--color-danger-soft)',  fg: 'var(--color-danger-text)' },
}

export function EventRow({ kind, title, amount, onClick }: EventRowProps) {
  const k = kindStyles[kind]
  const sign = amount >= 0 ? '+' : '-'
  const amountText = `${sign}${formatMoney(Math.abs(amount))}`
  const amountColor =
    amount >= 0 ? 'var(--color-success-text)' :
    kind === 'expense' || kind === 'print' ? 'var(--color-danger-text)' : 'var(--color-text)'

  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    cursor: onClick ? 'pointer' : 'default',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
  }
  const iconStyle: CSSProperties = {
    width: 24, height: 24, borderRadius: 'var(--radius-sm)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: k.bg, color: k.fg, fontSize: 11, fontWeight: 700,
    flexShrink: 0,
  }
  const titleStyle: CSSProperties = {
    color: 'var(--color-text)', fontSize: 'var(--font-size-sm)', flex: 1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }
  const amountStyle: CSSProperties = {
    color: amountColor, fontSize: 'var(--font-size-sm)', fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  }

  const content = (
    <>
      <span style={iconStyle}>{k.glyph}</span>
      <span style={titleStyle}>{title}</span>
      <span style={amountStyle}>{amountText}</span>
    </>
  )
  if (onClick) return <button type="button" style={rowStyle} onClick={onClick}>{content}</button>
  return <div style={rowStyle}>{content}</div>
}
